<template>

<!--
	The archive: every past puzzle, newest first, as a grid of cards (No. + date,
	with a small check when the player has solved it). A card routes to
	/archive/:date, where Daily.vue replays it in unranked practice mode. A quiet
	banner reminds the player practice doesn't touch their streak. Chrome wraps it.
-->

<div class="archive">

	<app-topbar v-on:signin="$emit('signin')" />

	<main class="archive-body">

		<div class="archive-card">

			<h1 class="archive-title">Archive</h1>
			<p class="archive-banner">Practice mode: your streak isn't affected.</p>

			<div v-if="loading" class="archive-state">Loading…</div>

			<div v-else-if="!days.length" class="archive-empty">
				<app-mark :size="42" />
				<div class="archive-empty-head">No past puzzles yet</div>
				<p class="archive-empty-sub">Once a few days of puzzles have run, they’ll collect here to replay in practice mode. Start with today’s.</p>
				<router-link to="/" class="archive-empty-cta">Play today’s puzzle</router-link>
			</div>

			<template v-else>

				<!-- filter toolbar: YOUR-RESULT chips (signed-in only — they key off
				     the player's own results) and community DIFFICULTY chips. Each row
				     is single-select; the two combine (e.g. Unplayed + Hard). -->
				<div class="archive-filters">

					<div v-if="signedIn" class="archive-filter">
						<span class="archive-filter-label">You</span>
						<div class="archive-toggle" role="group" aria-label="Filter by your result">
							<button
								v-for="opt in resultOptions"
								:key="opt.key"
								type="button"
								class="archive-toggle-btn"
								:class="{ 'is-active': resultFilter === opt.key }"
								v-on:click="resultFilter = opt.key"
							>{{ opt.label }}</button>
						</div>
					</div>

					<div class="archive-filter">
						<span class="archive-filter-label">Difficulty</span>
						<div class="archive-toggle" role="group" aria-label="Filter by difficulty across all players">
							<button
								v-for="opt in difficultyOptions"
								:key="opt.key"
								type="button"
								class="archive-toggle-btn"
								:class="{ 'is-active': difficultyFilter === opt.key }"
								v-on:click="difficultyFilter = opt.key"
							>{{ opt.label }}</button>
						</div>
					</div>

				</div>

				<!-- MOBILE: the same two filters as compact side-by-side dropdowns
				     (five-segment toggles don't fit two-up on a phone). v-model binds
				     the same state, so a selection survives a viewport change. -->
				<div class="archive-filters-mobile">

					<label v-if="signedIn" class="archive-select">
						<span class="archive-select-cap">You</span>
						<select v-model="resultFilter">
							<option v-for="opt in resultOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
						</select>
					</label>

					<label class="archive-select">
						<span class="archive-select-cap">Difficulty</span>
						<select v-model="difficultyFilter">
							<option v-for="opt in difficultyOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
						</select>
					</label>

				</div>

				<div v-if="filteredDays.length" class="archive-grid">
					<router-link
						v-for="day in filteredDays"
						:key="day.date"
						:to="'/archive/' + day.date"
						class="archive-day"
					>
						<!-- spoiler-free thumbnail: the box art's 8-colour palette as a
						     4x2 swatch grid (falls back to the tile shimmer if absent) -->
						<div v-if="day.palette && day.palette.length" class="archive-thumb archive-thumb-palette" aria-hidden="true">
							<span
								v-for="(c, i) in day.palette.slice(0, 8)"
								:key="i"
								class="archive-swatch"
								:style="{ background: c }"
							></span>
						</div>
						<div v-else class="archive-thumb" aria-hidden="true">
							<span v-for="n in 16" :key="n" class="archive-thumb-tile"></span>
						</div>
						<div class="archive-day-foot">
							<div class="archive-day-meta">
								<div class="archive-day-no">Day #{{ day.number }}</div>
								<div class="archive-day-date">{{ day.date }}</div>
							</div>
							<span v-if="day.score != null" class="archive-day-pill is-ranked" aria-label="Your score">{{ day.score }}</span>
							<span v-else-if="day.practiceScore != null" class="archive-day-pill is-practice" aria-label="Practice score">{{ day.practiceScore }}</span>
							<span v-else class="archive-day-unsolved" aria-hidden="true">—</span>
						</div>
						<!-- overall metrics across all players (shown even if unplayed):
						     value over a small label, the two rates as ring gauges, then
						     the full guess distribution as a bottom-anchored bar strip -->
						<template v-if="day.metrics && day.metrics.plays">
							<div class="archive-stats">
								<archive-stat :value="day.metrics.plays" label="played" />
								<archive-stat :value="day.metrics.solve_rate" suffix="%" label="solved" ring color="var(--correct)" />
								<archive-stat :value="day.metrics.avg_tiles || 0" label="guesses" />
								<archive-stat :value="day.metrics.oneshot_rate || 0" suffix="%" label="one-shot" ring color="#b8860b" />
							</div>
							<archive-dist
								:dist="day.metrics.dist"
								:self-guesses="day.self ? day.self.guesses : null"
								:self-solved="!!(day.self && day.self.solved)"
							/>
						</template>
						<div v-else class="archive-stats-empty">No plays yet</div>
					</router-link>
				</div>

				<div v-else class="archive-state">No days match these filters.</div>

			</template>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

import api from '../service/api'
import Stat from '../components/archive/Stat.vue'
import Distribution from '../components/archive/Distribution.vue'

// fixed community solve-rate (%) bands for the difficulty filter: hard is under
// HARD_MAX, easy is EASY_MIN and up, medium is everything between.
var HARD_MAX = 70
var EASY_MIN = 91

export default {

	name: 'Archive',

	components: {
		'archive-stat': Stat,
		'archive-dist': Distribution
	},

	data: function() {

		return {
			days: [],
			loading: false,
			// single-select filters; both default to "show everything"
			resultFilter: 'all',
			difficultyFilter: 'any',
			// YOUR-RESULT options (signed-in only) and community DIFFICULTY options
			resultOptions: [
				{ key: 'all',      label: 'All' },
				{ key: 'unplayed', label: 'Unplayed' },
				{ key: 'solved',   label: 'Solved' },
				{ key: 'failed',   label: 'Failed' },
				{ key: 'oneshot',  label: '1-shot' }
			],
			difficultyOptions: [
				{ key: 'any',    label: 'Any' },
				{ key: 'hard',   label: 'Hard' },
				{ key: 'medium', label: 'Medium' },
				{ key: 'easy',   label: 'Easy' }
			]
		}

	},

	computed: {

		// the your-result chips only make sense for a signed-in player (a guest has
		// no per-day result), so that whole group is hidden when signed out
		signedIn: function() {

			return this.$store.getters['account/signedIn']

		},

		// the visible cards after applying both single-select filters
		filteredDays: function() {

			var self = this

			return this.days.filter(function(day) {

				return self.matchesResult(day) && self.matchesDifficulty(day)

			})

		}

	},

	created: function() {

		this.fetch()

	},

	methods: {

		// YOUR-RESULT filter. Solved/Failed/1-shot key off the player's own LIVE
		// result (day.self); "Unplayed" means neither a live nor a practice score.
		matchesResult: function(day) {

			switch (this.resultFilter) {

				case 'unplayed':
					return day.score == null && day.practiceScore == null

				case 'solved':
					return !!(day.self && day.self.solved)

				case 'failed':
					return !!(day.self && !day.self.solved)

				case 'oneshot':
					return !!(day.self && day.self.solved && day.self.guesses === 1)

				default:
					return true

			}

		},

		// community DIFFICULTY filter, by fixed solve-rate band: hard < HARD_MAX,
		// easy >= EASY_MIN, medium in between. Days with no plays can't be classed,
		// so they only appear under "Any".
		matchesDifficulty: function(day) {

			if (this.difficultyFilter === 'any') return true

			var m = day.metrics

			if (!m || !m.plays) return false

			var rate = m.solve_rate

			if (this.difficultyFilter === 'hard')   return rate < HARD_MAX
			if (this.difficultyFilter === 'medium') return rate >= HARD_MAX && rate < EASY_MIN
			if (this.difficultyFilter === 'easy')   return rate >= EASY_MIN

			return true

		},

		fetch: function() {

			var self = this

			this.loading = true

			return api.request('archive', {}).then(function(json) {

				self.loading = false

				var days = (json && json.days) || []

				// newest first — sort defensively in case the server doesn't
				self.days = days.slice().sort(function(a, b) {

					return (b.date || '').localeCompare(a.date || '')

				})

			})

		}

	}

}

</script>

<style scoped>

.archive {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

.archive-body {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 20px 16px;
}

.archive-card {
	width: 100%;
	max-width: 440px;
}

.archive-title {
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0;
	color: var(--ink);
}

.archive-banner {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted);
	margin: 5px 0 18px;
}

/* MOBILE: centre the title + description */
@media (max-width: 599px) {

	.archive-title,
	.archive-banner {
		text-align: center;
	}

}

.archive-state {
	padding: 28px 2px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-align: center;
}

/* empty state: mark + heading + blurb + CTA back to today's puzzle */
.archive-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 40px 20px 36px;
}

.archive-empty-head {
	margin-top: 16px;
	font-family: var(--font-display);
	font-size: 20px;
	font-weight: 600;
	color: var(--ink);
}

.archive-empty-sub {
	margin: 7px auto 0;
	max-width: 320px;
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--muted-2);
}

.archive-empty-cta {
	margin-top: 18px;
	display: inline-flex;
	align-items: center;
	height: 42px;
	padding: 0 22px;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
}

.archive-empty-cta:hover {
	background: var(--accent);
}

/* filter toolbar: two segmented toggles (your-result + difficulty), each led by
   a small label naming whose results it filters */
.archive-filters {
	display: flex;
	flex-wrap: wrap;
	gap: 10px 18px;
	margin-bottom: 16px;
}

.archive-filter {
	display: flex;
	align-items: center;
	gap: 8px;
	max-width: 100%;
}

/* "You" / "Difficulty" */
.archive-filter-label {
	flex: none;
	font-family: var(--font-mono);
	font-size: 9px;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--muted);
}

/* the segmented control: options joined on one track, active segment filled.
   Scrolls rather than wraps if a track is too wide for a narrow card. */
.archive-toggle {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 2px;
	background: #efece5;
	border: 1px solid var(--hairline);
	border-radius: 999px;
	max-width: 100%;
	overflow-x: auto;
	scrollbar-width: none;
}

.archive-toggle::-webkit-scrollbar {
	display: none;
}

.archive-toggle-btn {
	flex: none;
	appearance: none;
	border: none;
	background: transparent;
	color: var(--muted-2);
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.02em;
	white-space: nowrap;
	padding: 5px 12px;
	border-radius: 999px;
	cursor: pointer;
	transition: background 120ms ease, color 120ms ease;
}

.archive-toggle-btn:hover {
	color: var(--ink);
}

.archive-toggle-btn.is-active {
	background: var(--ink);
	color: var(--paper);
}

/* the mobile dropdown variant is hidden on desktop (the toggles show instead) */
.archive-filters-mobile {
	display: none;
}

/* MOBILE: swap the segmented toggles for two compact side-by-side selects,
   styled to echo the toggle (warm track, hairline border, mono type) */
@media (max-width: 599px) {

	.archive-filters {
		display: none;
	}

	.archive-filters-mobile {
		display: flex;
		gap: 10px;
		margin-bottom: 16px;
	}

	.archive-select {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 5px;
	}

	.archive-select-cap {
		font-family: var(--font-mono);
		font-size: 9px;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--muted);
		text-align: center;
	}

	.archive-select select {
		width: 100%;
		appearance: none;
		-webkit-appearance: none;
		background-color: #efece5;
		background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%239a9284' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
		background-repeat: no-repeat;
		background-position: right 12px center;
		border: 1px solid var(--hairline);
		border-radius: 999px;
		font-family: var(--font-mono);
		font-size: 12px;
		color: var(--ink);
		padding: 9px 30px 9px 14px;
		cursor: pointer;
	}

}

.archive-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 12px;
}

/* wider phones (and up) get two per row; narrow phones stay one per row */
@media (min-width: 400px) {

	.archive-grid {
		grid-template-columns: 1fr 1fr;
	}

}

.archive-day {
	display: block;
	border: 1px solid var(--hairline);
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	text-decoration: none;
	color: inherit;
	transition: border-color 140ms ease, box-shadow 140ms ease;
}

.archive-day:hover {
	border-color: var(--muted-2);
	box-shadow: var(--shadow);
}

.archive-thumb {
	height: 78px;
	background: repeating-linear-gradient(135deg, #cdbfa6 0 12px, #c4b69b 12px 24px);
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: repeat(4, 1fr);
}

.archive-thumb-tile {
	border: 0.5px solid rgba(239, 233, 220, 0.5);
	background: #efe9dc;
	opacity: 0.55;
}

/* palette thumbnail: 8 dominant colours as a 4x2 swatch grid */
.archive-thumb-palette {
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: repeat(2, 1fr);
	background: none;
}

.archive-swatch {
	display: block;
	width: 100%;
	height: 100%;
}

.archive-day-foot {
	padding: 10px 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.archive-day-no {
	font-family: var(--font-mono);
	font-size: 12px;
	color: var(--ink);
}

.archive-day-date {
	font-family: var(--font-body);
	font-size: 11px;
	color: var(--muted);
	margin-top: 1px;
}

/* score pill: green for the player's ranked (live) score, grey for a practice
   score (day only practiced). Shows the score itself instead of a plain tick. */
.archive-day-pill {
	flex: none;
	min-width: 28px;
	padding: 2px 8px;
	border-radius: 999px;
	text-align: center;
	font-family: var(--font-mono);
	font-size: 11px;
	font-weight: 500;
	line-height: 1.4;
}

.archive-day-pill.is-ranked {
	background: #eef1ea;
	border: 1px solid #c4d2bd;
	color: var(--correct);
}

.archive-day-pill.is-practice {
	background: #efece5;
	border: 1px solid var(--hairline);
	color: var(--muted-2);
}

.archive-day-unsolved {
	font-family: var(--font-mono);
	font-size: 13px;
	color: var(--tile-2);
}

/* the visual metric strip: four stats (value over label), the rates as rings */
.archive-stats {
	display: flex;
	align-items: flex-start;
	gap: 4px;
	padding: 2px 10px 12px;
}

.archive-stats-empty {
	padding: 0 12px 12px;
	margin-top: -2px;
	font-family: var(--font-mono);
	font-size: 9.5px;
	letter-spacing: 0.02em;
	color: var(--muted);
}

@media (min-width: 720px) {

	.archive-card {
		max-width: 720px;
	}

	.archive-grid {
		grid-template-columns: repeat(3, 1fr);
	}

}

</style>
